/**
 * BrokerStatusBadge — badge cohérent pour les 3 statuts user broker.
 *
 * Mapping `app.user_status` (active|pending|disabled) → couleur tone :
 *   - active   → emerald (parité Companies active)
 *   - pending  → amber (en attente d'activation invitation)
 *   - disabled → red (désactivé Auth)
 *
 * Pattern parité `CompanyStatusBadge` + `ProjectStatusBadge` (dot + label).
 */

import { useTranslation } from "react-i18next";

type Tone = "emerald" | "amber" | "red" | "slate";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
};

function toneFor(status: string): Tone {
  switch (status) {
    case "active":
      return "emerald";
    case "pending":
      return "amber";
    case "disabled":
      return "red";
    default:
      return "slate";
  }
}

type Props = {
  status: string;
};

export function BrokerStatusBadge({ status }: Props) {
  const { t } = useTranslation();
  const tone = TONE[toneFor(status)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2.5 py-0.5 text-xs font-medium ${tone.text}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      {t(`brokers.list.statuses.${status}`, { defaultValue: status })}
    </span>
  );
}
